import * as React from 'react';
import { cx } from 'linaria';
import * as RadixTabs from '@radix-ui/react-tabs';

import { TabsProps, TabsComponent, TabsPanelProps } from './tabs.types';
import { flattenReactChildren } from '../utils/flatten-react-children';
import { makeTestId } from '../utils/common';
import { Typography } from '../index';
import * as styles from './tabs.styles';

export const Tabs: TabsComponent = React.forwardRef<HTMLDivElement, React.PropsWithChildren<TabsProps>>(
    (props, ref) => {
        const { children, openValue, defaultOpenValue, testId, onChange, className } = props;

        const flatChildren = flattenReactChildren(children);

        const headers = flatChildren.map((child: React.ReactElement<TabsPanelProps>, index) => {
            const { header, value: panelValue } = child.props;

            const value = panelValue ?? `tab-${index}`;
            const key = `tab-${index}-trigger`;

            return (
                <RadixTabs.Trigger key={key} value={value} asChild>
                    <button
                        className={cx(styles.trigger, styles.states)}
                        data-testid={makeTestId(testId, `trigger-${index}`)}
                    >
                        <Typography variant="body-1">{header}</Typography>
                    </button>
                </RadixTabs.Trigger>
            );
        });

        const panels = flatChildren.map((child: React.ReactElement<TabsPanelProps>, index) => {
            const { value: panelValue, testId: panelTestId } = child.props;

            const value = panelValue ?? `tab-${index}`;
            const key = `tab-${index}-content`;

            return (
                <RadixTabs.Content key={key} value={value} asChild>
                    <TabsPanel {...child.props} testId={panelTestId ?? makeTestId(testId, `panel-${index}`)} />
                </RadixTabs.Content>
            );
        });

        return (
            <RadixTabs.Root
                activationMode="manual"
                value={openValue}
                defaultValue={defaultOpenValue ?? 'tab-0'}
                onValueChange={onChange}
                asChild
            >
                <div ref={ref} className={className} data-testid={testId}>
                    <div className={cx(styles.headerTabs)}>
                        <RadixTabs.List>{headers}</RadixTabs.List>
                    </div>

                    {panels}
                </div>
            </RadixTabs.Root>
        );
    },
);

// Components
export const TabsPanel = React.forwardRef<HTMLDivElement, React.PropsWithChildren<TabsPanelProps>>((props, ref) => {
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const { children, testId, header: __header, ...passThroughProps } = props;

    return (
        <div {...passThroughProps} ref={ref} data-testid={testId}>
            {children}
        </div>
    );
});

TabsPanel.displayName = 'Tabs.Panel';

Tabs.displayName = 'Tabs';
Tabs.Panel = TabsPanel;
